import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getAnalyticsDashboard, getDocuments } from '../../services/api';
import './AdminModern.css';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [analyticsData, docsData] = await Promise.all([
        getAnalyticsDashboard(),
        getDocuments()
      ]);
      setAnalytics(analyticsData);
      setDocuments(Array.isArray(docsData) ? docsData : (docsData.results || []));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProcessedDocsCount = () => documents.filter(d => d.is_processed).length;
  const getPendingDocsCount = () => documents.filter(d => !d.is_processed).length;
  const getScrapedDocsCount = () => documents.filter(d => d.source_type === 'url').length;

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-content">
          <div className="loading-spinner-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="dashboard-error">
        <div className="error-content">
          <div className="error-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h2>Unable to load analytics</h2>
          <p>There was a problem fetching the dashboard data</p>
          <button onClick={loadData} className="retry-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Quick Stats Banner */}
      <div className="stats-banner">
        <div className="stat-item">
          <div className="stat-icon blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-number">{analytics.overview.total_chats}</span>
            <span className="stat-label">Total Chats</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon purple">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-number">{analytics.overview.total_messages}</span>
            <span className="stat-label">Messages</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-number">{analytics.overview.total_documents}</span>
            <span className="stat-label">Documents</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon orange">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-number">{getScrapedDocsCount()}</span>
            <span className="stat-label">Web Sources</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <button
          className={`dashboard-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="9"/>
            <rect x="14" y="3" width="7" height="5"/>
            <rect x="14" y="12" width="7" height="9"/>
            <rect x="3" y="16" width="7" height="5"/>
          </svg>
          Overview
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
          </svg>
          Knowledge Base
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
          Performance
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="dashboard-grid">
          {/* Activity Metrics */}
          <div className="dashboard-card activity-card">
            <div className="card-header">
              <h3>Recent Activity</h3>
              <span className="card-badge">Last 30 Days</span>
            </div>
            <div className="activity-metrics">
              <div className="metric-row">
                <div className="metric-label">
                  <span className="metric-dot blue"></span>
                  New Conversations
                </div>
                <div className="metric-value">{analytics.recent_activity.chats}</div>
                <div className="metric-bar">
                  <div
                    className="metric-fill blue"
                    style={{
                      width: `${Math.min((analytics.recent_activity.chats / Math.max(analytics.overview.total_chats, 1)) * 100, 100)}%`
                    }}
                  ></div>
                </div>
              </div>
              <div className="metric-row">
                <div className="metric-label">
                  <span className="metric-dot purple"></span>
                  Messages Generated
                </div>
                <div className="metric-value">{analytics.recent_activity.messages}</div>
                <div className="metric-bar">
                  <div
                    className="metric-fill purple"
                    style={{
                      width: `${Math.min((analytics.recent_activity.messages / Math.max(analytics.overview.total_messages, 1)) * 100, 100)}%`
                    }}
                  ></div>
                </div>
              </div>
              <div className="metric-row">
                <div className="metric-label">
                  <span className="metric-dot green"></span>
                  Documents Added
                </div>
                <div className="metric-value">{analytics.recent_activity.documents}</div>
                <div className="metric-bar">
                  <div
                    className="metric-fill green"
                    style={{
                      width: `${Math.min((analytics.recent_activity.documents / Math.max(analytics.overview.total_documents, 1)) * 100, 100)}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Chart Card */}
          <div className="dashboard-card chart-card">
            <div className="card-header">
              <h3>Daily Engagement</h3>
              <span className="card-badge">Last 7 Days</span>
            </div>
            <div className="chart-container">
              {analytics.activity_by_day.map((day, index) => {
                const maxMessages = Math.max(...analytics.activity_by_day.map(d => d.messages), 1);
                const heightPercent = (day.messages / maxMessages) * 100;
                return (
                  <div key={index} className="chart-column">
                    <div className="chart-bar-container">
                      <div
                        className="chart-bar"
                        style={{ height: `${Math.max(heightPercent, 8)}%` }}
                      >
                        {day.messages > 0 && (
                          <span className="chart-tooltip">{day.messages}</span>
                        )}
                      </div>
                    </div>
                    <span className="chart-label">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Conversation */}
          {analytics.most_active_chat && (
            <div className="dashboard-card highlight-card">
              <div className="highlight-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>
              <div className="highlight-info">
                <span className="highlight-label">Most Active Conversation</span>
                <h4>{analytics.most_active_chat.title}</h4>
                <span className="highlight-stat">{analytics.most_active_chat.message_count} messages</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="documents-overview">
          <div className="docs-stats-grid">
            <div className="docs-stat-card">
              <div className="docs-stat-icon total">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div className="docs-stat-content">
                <span className="docs-stat-number">{documents.length}</span>
                <span className="docs-stat-label">Total Documents</span>
              </div>
            </div>
            <div className="docs-stat-card">
              <div className="docs-stat-icon processed">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <div className="docs-stat-content">
                <span className="docs-stat-number">{getProcessedDocsCount()}</span>
                <span className="docs-stat-label">Processed</span>
              </div>
            </div>
            <div className="docs-stat-card">
              <div className="docs-stat-icon pending">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="docs-stat-content">
                <span className="docs-stat-number">{getPendingDocsCount()}</span>
                <span className="docs-stat-label">Processing</span>
              </div>
            </div>
            <div className="docs-stat-card">
              <div className="docs-stat-icon scraped">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </div>
              <div className="docs-stat-content">
                <span className="docs-stat-number">{getScrapedDocsCount()}</span>
                <span className="docs-stat-label">Web Scraped</span>
              </div>
            </div>
          </div>

          <div className="dashboard-card recent-docs-card">
            <div className="card-header">
              <h3>Recent Documents</h3>
            </div>
            {documents.length === 0 ? (
              <div className="empty-docs">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <p>No documents in the knowledge base yet</p>
                <span>Upload documents or scrape websites to get started</span>
              </div>
            ) : (
              <div className="docs-list">
                {documents.slice(0, 5).map((doc) => (
                  <div key={doc.id} className="doc-list-item">
                    <div className="doc-icon">
                      {doc.file_type === 'url' ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      )}
                    </div>
                    <div className="doc-details">
                      <span className="doc-title">{doc.title}</span>
                      <span className="doc-meta">{doc.chunks_count} chunks</span>
                    </div>
                    <div className={`doc-status ${doc.is_processed ? 'ready' : 'processing'}`}>
                      {doc.is_processed ? 'Ready' : 'Processing'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="performance-grid">
          <div className="performance-card">
            <div className="perf-icon engagement">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
            </div>
            <div className="perf-content">
              <span className="perf-value">
                {analytics.overview.total_chats > 0
                  ? Math.round((analytics.recent_activity.chats / analytics.overview.total_chats) * 100)
                  : 0}%
              </span>
              <span className="perf-label">Engagement Rate</span>
              <span className="perf-desc">New conversations in last 30 days</span>
            </div>
          </div>

          <div className="performance-card">
            <div className="perf-icon depth">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
            </div>
            <div className="perf-content">
              <span className="perf-value">
                {analytics.overview.total_chats > 0
                  ? Math.round(analytics.overview.total_messages / analytics.overview.total_chats)
                  : 0}
              </span>
              <span className="perf-label">Avg. Messages per Chat</span>
              <span className="perf-desc">Average conversation depth</span>
            </div>
          </div>

          <div className="performance-card">
            <div className="perf-icon knowledge">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
            </div>
            <div className="perf-content">
              <span className="perf-value">{getProcessedDocsCount()}/{documents.length}</span>
              <span className="perf-label">Knowledge Base Status</span>
              <span className="perf-desc">Processed documents ratio</span>
            </div>
          </div>

          <div className="performance-card">
            <div className="perf-icon health">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div className="perf-content">
              <span className="perf-value status-ok">Healthy</span>
              <span className="perf-label">System Status</span>
              <span className="perf-desc">All systems operational</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
